import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Menu,
  ListItemText,
  ListItemIcon,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { TreeView, TreeItem } from "@mui/x-tree-view";
import { DndContext, useDraggable, useDroppable, closestCenter } from '@dnd-kit/core';
import { Add, Close, CloudUpload, ExpandMore, ChevronRight, ViewColumn, FilterList, DensityMedium, Check, ArrowUpward } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { GlobalStyles } from '@mui/material';
import {
  fetchCategoryTree,
  createCategoryTree,
  updateCategoryTree,
  deleteCategoryTree
} from "../../Redux/Reducers/CategoryTreeReducer";
import usePermissions from "../../hooks/usePermissions";
import axiosInstance from "../../utils/axiosInstance";

const MAX_LEVELS = 2; // For preventing more than 2 levels of nesting

const MODULE_KEY = "category";

const getImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === 'string') return img;
  return img.cdnUrl || img.url || null;
};

function CategoryTreeManagement() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { categories = [], loading } = useSelector((state) => state.categoryTree);
  const { hasAction } = usePermissions();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [homeImagePreview, setHomeImagePreview] = useState(null);
  const [searchImagePreview, setSearchImagePreview] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  const [densityAnchorEl, setDensityAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    english: true,
    french: true,
    actions: true,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [density, setDensity] = useState("standard");

  // const [confirmDialog, setConfirmDialog] = useState({
  //     open: false,
  //     id: null,
  //     newStatus: null,
  // });

  const [paginationLoadMore, setPaginationLoadMore] = useState({
    hasMore: false,
    total_pages: 1,
    page: 1
  });

  const rows = useMemo(() => {
    if (!Array.isArray(categories)) return [];

    return categories ? categories.map((cat, index) => ({
      ...cat,
      id: cat.id,
      backendId: cat.id,
      parentId: cat.parentId,
      sno: index + 1,
      name: cat.translations?.find(t => t.language === "en")?.name || "",
      translations: cat.translations || [],
      isActive: cat.isActive,
      level: cat.level,
      children: cat.children ?? [],
    })) : [];
  }, [categories]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "active") return rows.filter((r) => r.isActive);
    if (statusFilter === "inactive") return rows.filter((r) => !r.isActive);
    return rows;
  }, [rows, statusFilter]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nameEn: "",
      nameFr: "",
      isActive: true,
      homeImage: null,
      searchImage: null,
      backgroundColor: "#000000",
      backgroundColor2: "#000000",
      type: "PODCAST",
      gradientText: ""
    },
  });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleView = (row) => {
    setSelectedCategory(row);
    setViewModalOpen(true);
  };

  const watchNameEn = watch("nameEn", "");
  const watchNameFr = watch("nameFr", "");
  const watchBackgroundColor = watch("backgroundColor", "#000000");
  const watchBackgroundColor2 = watch("backgroundColor2", "#000000");

  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(fetchCategoryTree({
        page: paginationLoadMore.page,
        limit: 10,
        search: searchText
      }))
        .unwrap()
        .then((res) => {
          const totalPages = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
          const currentPage = res?.pagination?.page || res?.pagination?.current_page || 1;

          setPaginationLoadMore(prev => ({
            ...prev,
            total_pages: totalPages,
            hasMore: currentPage < totalPages
          }));
        });
    }, 400);

    return () => clearTimeout(delay);
  }, [dispatch, paginationLoadMore.page, searchText]);

  useEffect(() => {
    setPaginationLoadMore({
      hasMore: false,
      total_pages: 1,
      page: 1
    });
  }, [searchText]);

  const handleOpenModal = (e = null, node = null, row = null) => {
    if (e) e.stopPropagation();

    if (row) {
      setEditingCategory(row);
      setSelectedParent(null);

      const homeImgUrl = getImageUrl(row?.featuredImage);
      const searchImgUrl = getImageUrl(row?.image);

      setHomeImagePreview(homeImgUrl);
      setSearchImagePreview(searchImgUrl);

      reset({
        nameEn: getCategoryName(row, "en"),
        nameFr: getCategoryName(row, "fr"),
        isActive: row.isActive,
        homeImage: homeImgUrl,
        searchImage: searchImgUrl,
        backgroundColor: row.bgColor1 || row.backgroundColor || "#000000",
        backgroundColor2: row.bgColor2 || row.backgroundColor2 || "#000000",
        type: row.type || "PODCAST",
        gradientText: row.gradientText || ""
      });
    } else {
      setEditingCategory(null);
      setSelectedParent(node || null);
      setHomeImagePreview(null);
      setSearchImagePreview(null);

      reset({
        nameEn: "",
        nameFr: "",
        isActive: true,
        homeImage: null,
        searchImage: null,
        backgroundColor: "#000000",
        backgroundColor2: "#000000",
        type: "PODCAST",
        gradientText: ""
      });
    }

    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  // const handleImageChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     setValue("image", file);
  //     setImagePreview(URL.createObjectURL(file));
  //   }
  // };


  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleHomeImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Image size validation
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(
          t(
            "image_size_limit",
            "Image must be under 5MB"
          )
        );
        return;
      }
      setValue("homeImage", file);
      setHomeImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSearchImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Image size validation
      if (file.size > MAX_IMAGE_SIZE) {
        toast.error(
          t(
            "image_size_limit",
            "Image must be under 5MB"
          )
        );
        return;
      }
      setValue("searchImage", file);
      setSearchImagePreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    const formData = new FormData();

    formData.append("name", data.nameEn);
    formData.append("type", data.type || "PODCAST");
    formData.append("isActive", data.isActive);

    const translations = [
      { lang: "en", name: data.nameEn },
      { lang: "fr", name: data.nameFr }
    ];

    formData.append("translations", JSON.stringify(translations));
    formData.append("bgColor1", data.backgroundColor);
    formData.append("bgColor2", data.backgroundColor2);
    formData.append("gradientText", `linear-gradient(69deg, ${data.backgroundColor || '#000000'} 0%, ${data.backgroundColor2 || '#000000'} 100%)`);

    // ADD THIS LOGIC
    if (!editingCategory) {
      if (selectedParent) {
        formData.append("parentId", selectedParent.id);
        formData.append("level", selectedParent.level + 1);
      } else {
        // formData.append("parentId", null);
        formData.append("level", 1);
      }
    }

    if (data.homeImage) {
      if (data.homeImage instanceof File) {
        formData.append("featuredImage", data.homeImage);
      } else if (typeof data.homeImage === "string") {
        try {
          const response = await fetch(data.homeImage);
          if (!response.ok) throw new Error("Network response was not ok");
          const blob = await response.blob();
          const file = new File([blob], "image.png", { type: blob.type || 'image/png' });
          formData.append("image", file);
        } catch (e) {
          toast.error(t('reselect_image', "Please manually re-select the Home Image. The server requires the file, but browser security prevents auto-fetching it."));
          setSubmitting(false);
          return;
        }
      }
    }

    if (data.searchImage) {
      if (data.searchImage instanceof File) {
        formData.append("image", data.searchImage);
      } else if (typeof data.searchImage === "string") {
        try {
          const response = await fetch(data.searchImage);
          if (!response.ok) throw new Error("Network response was not ok");
          const blob = await response.blob();
          const file = new File([blob], "featured_image.png", { type: blob.type || 'image/png' });
          formData.append("featuredImage", file);
        } catch (e) {
          toast.error(t('reselect_search_image', "Please manually re-select the Search Image. The server requires the file, but browser security prevents auto-fetching it."));
          setSubmitting(false);
          return;
        }
      }
    }

    try {
      if (editingCategory) {
        await dispatch(
          updateCategoryTree({
            id: editingCategory.id,
            formData: formData
          })
        ).unwrap();

        toast.success(t('category_updated', "Category updated"));
      } else {
        await dispatch(createCategoryTree(formData)).unwrap();
        toast.success(t('category_created', "Category created"));
      }

      handleCloseModal();
      dispatch(fetchCategoryTree({
        limit: 10,
        search: searchText,
        page: paginationLoadMore.page
      }
      ));

    } catch (err) {
      // Error is already handled by axiosInstance interceptor
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteCategoryTree({ id: selectedId })).unwrap();
      toast.success(t('category_deleted', 'Category deleted'));
      dispatch(fetchCategoryTree({
        limit: 10,
        search: searchText,
        page: paginationLoadMore.page
      }
      ));
    } catch {
      toast.error(t('delete_failed', 'Delete failed'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const getCategoryName = (cat, lang = "en") => {
    return cat.translations?.find(t => t.lang === lang || t.language === lang)?.name || "";
  };

  const renderCategoryImage = (cat) => {
    if (!cat.image || !cat.image.url) {
      return <Avatar variant="square" sx={{ width: 24, height: 24, bgcolor: 'rgba(230, 210, 249, 0.2)', borderRadius: '4px' }}>
        {cat.name?.charAt(0) || '?'}
      </Avatar>;
    }

    return <Avatar
      variant="square"
      src={cat.image.url}
      alt={cat.name}
      sx={{
        width: 24,
        height: 24,
        border: '2px solid rgba(230, 210, 249, 0.2)',
        borderRadius: '4px'
      }}
    />;
  };

  const handleMoveToRoot = async (e, node) => {
    e.stopPropagation();
    const formData = new FormData();
    formData.append("name", getCategoryName(node, "en"));
    formData.append("isActive", node.isActive);

    const translations = [
      { lang: "en", name: getCategoryName(node, "en") },
      { lang: "fr", name: getCategoryName(node, "fr") }
    ];
    formData.append("translations", JSON.stringify(translations));
    formData.append("backgroundColor", node.backgroundColor || "#000000");
    formData.append("backgroundColor2", node.backgroundColor2 || "#000000");
    formData.append("gradientText", `linear-gradient(69deg, ${node.backgroundColor || node.bgColor1 || '#000000'} 0%, ${node.backgroundColor2 || node.bgColor2 || '#000000'} 100%)`);

    formData.append("level", 1);



    try {
      await dispatch(updateCategoryTree({ id: node.id, formData })).unwrap();
      toast.success(t('category_moved', 'Category moved to root successfully'));
      dispatch(fetchCategoryTree({ limit: 10, search: searchText, page: paginationLoadMore.page }));
    } catch (err) {
      // Error is already handled by axiosInstance interceptor
      console.error(err);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeNode = active.data.current?.node;
    const overNode = over.data.current?.node;

    if (!activeNode || !overNode) {
      return;
    }

    if (String(activeNode.id) === String(overNode.id)) {
      return;
    }

    const activeParentId = activeNode.parentId || null;
    const overParentId = overNode.parentId || null;

    const findSiblings = (nodes, parentId) => {
      if (!Array.isArray(nodes)) {
        return [];
      }

      if (!parentId) {
        return nodes;
      }

      for (const node of nodes) {
        if (String(node.id) === String(parentId)) {
          return Array.isArray(node.children)
            ? node.children
            : [];
        }

        if (node.children?.length) {
          const result = findSiblings(node.children, parentId);

          if (result.length) {
            return result;
          }
        }
      }

      return [];
    };

    const overSiblings = findSiblings(
      rows,
      overParentId
    );

    const overIndex = overSiblings.findIndex(
      (node) =>
        String(node.id) === String(overNode.id)
    );

    if (overIndex === -1) {
      console.warn("Drop target not found");
      return;
    }

    const newPosition = overIndex + 1;

    try {
      const payload = {
        position: String(newPosition),
      };

      if (
        String(activeParentId || "") !==
        String(overParentId || "")
      ) {
        payload.parentId = overParentId;
      }

      console.log("Drag & Drop Payload:", {
        activeNode,
        overNode,
        payload,
      });

      await axiosInstance.patch(
        `/api/admin/level-categories/${activeNode.id}/position`,
        payload
      );

      toast.success(
        t(
          "position_updated",
          "Position updated successfully"
        )
      );

      dispatch(
        fetchCategoryTree({
          limit: 10,
          search: searchText,
          page: paginationLoadMore.page,
        })
      );
    } catch (error) {
      console.error(
        "Failed to update category position:",
        error
      );

      toast.error(
        t(
          "position_update_failed",
          "Failed to update category position"
        )
      );
    }
  };

  const DraggableCategoryNode = ({ node, currentDensity }) => {
    const {
      attributes,
      listeners,
      setNodeRef: setDragRef,
      transform,
      isDragging,
    } = useDraggable({
      id: `drag-${node.id}`,
      data: {
        type: "category",
        node,
      },
    });

    const {
      setNodeRef: setDropRef,
      isOver,
    } = useDroppable({
      id: `drop-${node.id}`,
      data: {
        type: "category",
        node,
      },
    });

    const dragStyle = transform
      ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
      : undefined;

    return (
      <Box
        ref={setDropRef}
        sx={{
          width: "100%",
          minWidth: 0,
          position: "relative",
          backgroundColor: isOver
            ? "rgba(255, 105, 180, 0.12)"
            : "transparent",
          border: isOver
            ? "1px dashed #FF69B4"
            : "1px solid transparent",
          transition: "background-color 0.2s ease",
        }}
      >
        <Box
          ref={setDragRef}
          style={dragStyle}
          {...listeners}
          {...attributes}
          sx={{
            display: "grid",
            gridTemplateColumns: treeGridColumns,
            alignItems: "center",
            columnGap: "5px",
            width: "100%",
            minHeight:
              currentDensity === "compact"
                ? "48px"
                : currentDensity === "comfortable"
                  ? "68px"
                  : "58px",
            boxSizing: "border-box",
            cursor: isDragging ? "grabbing" : "grab",
            opacity: isDragging ? 0.5 : 1,
            userSelect: "none",
          }}
        >
          {/* S.NO */}
          <Box
            sx={{
              textAlign: "center",
              fontSize: "14px",
              fontWeight: 600,
              color: "#fff",
            }}
          >
            {node.level === 1 ? node.sno : ""}
          </Box>

          {/* IMAGE */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              variant="square"
              src={
                getImageUrl(node.featuredImage) ||
                getImageUrl(node.image) ||
                node.imageUrl ||
                ""
              }
              alt={getCategoryName(node, "en")}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: "rgba(227, 214, 245, 0.2)",
                borderRadius: '5px'
              }}
            >
              {getCategoryName(node, "en")?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Box>

          {/* ENGLISH */}
          {visibleColumns.english && (
            <Box
              sx={{
                minWidth: 0,
                padding: "0 10px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "14px",
                color: "#fff",
              }}
            >
              {getCategoryName(node, "en") || "-"}
            </Box>
          )}

          {/* FRENCH */}
          {visibleColumns.french && (
            <Box
              sx={{
                minWidth: 0,
                padding: "0 10px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "14px",
                color: "#fff",
              }}
            >
              {getCategoryName(node, "fr") || "-"}
            </Box>
          )}

          {/* ACTIONS */}
          {visibleColumns.actions && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {/* ADD */}
              {hasAction(MODULE_KEY, "create") &&
                node.level < MAX_LEVELS && (
                  <IconButton
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(e, node);
                    }}
                    sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2)', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                  >
                    <Add sx={{ color: 'white' }} />
                  </IconButton>
                )}

              {/* VIEW */}
              {hasAction(MODULE_KEY, "read") && (
                <IconButton
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(node);
                  }}
                  sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2)', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                    <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                    <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                  </svg>
                </IconButton>
              )}

              {/* EDIT */}
              {hasAction(MODULE_KEY, "update") && (
                <IconButton
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(e, null, node);
                  }}
                  sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2)', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white" />
                  </svg>
                </IconButton>
              )}

              {/* DELETE */}
              {hasAction(MODULE_KEY, "delete") && (
                <IconButton
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(node.id);
                  }}
                  sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2)', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                    <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                    <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                    <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                    <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                    <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
                  </svg>
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderTree = (treeRows, level = 0) => {
    if (!Array.isArray(treeRows)) {
      return null;
    }

    return treeRows.map((category, index) => {
      const currentNode = {
        ...category,
        sno: category.sno || index + 1,
        level: category.level || level + 1,
        children: Array.isArray(category.children)
          ? category.children
          : [],
      };

      return (
        <TreeItem
          key={String(currentNode.id)}
          nodeId={String(currentNode.id)}
          label={
            <DraggableCategoryNode
              node={currentNode}
              currentDensity={density}
            />
          }
        >
          {currentNode.children.length > 0 &&
            renderTree(currentNode.children, level + 1)}
        </TreeItem>
      );
    });
  };

  const treeGridColumns = `
    55px
    55px
    ${visibleColumns.english ? 'minmax(150px, 1fr)' : ''}
    ${visibleColumns.french ? 'minmax(150px, 1fr)' : ''}
    ${visibleColumns.actions ? '180px' : ''}
`;

  return (
    <Box sx={{ p: '36px 24px 24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('category_management', 'Category Management')}</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search', 'Search')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                color: '#fff',
                height: '38px',
                '& fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 16px',
                color: '#FF69B4',
                fontSize: '15px',
                lineHeight: '18px',

              },
              '& input::placeholder': {
                color: 'rgba(255,255,255,0.6)',
                opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                ml: '20px'
              }
            }}
          />
          {hasAction(MODULE_KEY, 'create') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}
              sx={{
                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                color: '#fff',
                textTransform: 'none',
                backgroundColor: '#FF69B4 !important',
                borderRadius: '999px',
                fontSize: '15px',
                lineHeight: '24px',
                padding: '0',
                minWidth: '38px',
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}>
            </Button>
          )}
        </Box>
      </Box>


      <Box sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(230, 210, 249, 0.2)',
        borderRadius: '10px',
        px: '12px'
      }}>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            padding: '12px',
            margin: '0 -12px',
            backgroundColor: 'rgba(255,255,255,0.05)',
          }}
        >
          <Button
            size="small"
            startIcon={<ViewColumn />}
            onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
            sx={{
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            {t('columns', 'Columns')}
          </Button>
          <Button
            size="small"
            startIcon={<FilterList />}
            onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
            sx={{
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            {t('filters', 'Filters')}
          </Button>
          <Button
            size="small"
            startIcon={<DensityMedium />}
            onClick={(e) => setDensityAnchorEl(e.currentTarget)}
            sx={{
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            }}
          >
            {t('density', 'Density')}
          </Button>
        </Box>
        <Menu
          anchorEl={columnsAnchorEl}
          open={Boolean(columnsAnchorEl)}
          onClose={() => setColumnsAnchorEl(null)}
        >
          {[
            { key: "english", label: t('name_english', 'Name (English)') },
            { key: "french", label: t('name_french', 'Name (French)') },
            { key: "actions", label: t('actions', 'Actions') },
          ].map((item) => (
            <MenuItem
              key={item.key}
              onClick={() =>
                setVisibleColumns((prev) => ({ ...prev, [item.key]: !prev[item.key] }))
              }
            >
              <ListItemIcon>{visibleColumns[item.key] ? <Check fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={filtersAnchorEl}
          open={Boolean(filtersAnchorEl)}
          onClose={() => setFiltersAnchorEl(null)}
        >
          {[
            { key: "all", label: t('all', 'All') },
            { key: "active", label: t('active', 'Active') },
            { key: "inactive", label: t('inactive', 'Inactive') },
          ].map((item) => (
            <MenuItem
              key={item.key}
              onClick={() => {
                setStatusFilter(item.key);
                setFiltersAnchorEl(null);
              }}
            >
              <ListItemIcon>{statusFilter === item.key ? <Check fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={densityAnchorEl}
          open={Boolean(densityAnchorEl)}
          onClose={() => setDensityAnchorEl(null)}
        >
          {[
            { key: "compact", label: t('compact', 'Compact') },
            { key: "standard", label: t('standard', 'Standard') },
            { key: "comfortable", label: t('comfortable', 'Comfortable') },
          ].map((item) => (
            <MenuItem
              key={item.key}
              onClick={() => {
                setDensity(item.key);
                setDensityAnchorEl(null);
              }}
            >
              <ListItemIcon>{density === item.key ? <Check fontSize="small" /> : null}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        <Box sx={{
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
          "&::-webkit-scrollbar": {
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(230, 210, 249, 0.35)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(230, 210, 249, 0.55)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(230, 210, 249, 0.35) transparent",
        }}>
          <Box
            sx={{
              minWidth: '100%',
              px: "0",
              py: "10px",
              borderBottom: "1px solid rgba(230, 210, 249, 0.2)",
              color: "rgba(255,255,255,0.7)",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `
                  38px
                  ${treeGridColumns}
                `,

                alignItems: 'center',
                columnGap: '5px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >

              <Box />

              <Box
                sx={{
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                }}
              >
                S.No.
              </Box>

              <Box />

              {visibleColumns.english && (
                <Box
                  sx={{
                    fontSize: '14px',
                    color: '#fff',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('name_english', 'Name (English)')}
                </Box>
              )}

              {visibleColumns.french && (
                <Box
                  sx={{
                    fontSize: '14px',
                    color: '#fff',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('name_french', 'Name (French)')}
                </Box>
              )}

              {visibleColumns.actions && (
                <Box
                  sx={{
                    fontSize: '14px',
                    color: '#fff',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('actions', 'Actions')}
                </Box>
              )}
            </Box>
            {/* <Box sx={{ width: "38px" }}></Box>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: '10px' }}>
              <Box sx={{ width: '75px' }}></Box>
              {visibleColumns.english && <Box sx={{ flex: 1, paddingLeft: '10px', fontSize: '14px', color: '#fff', textAlign: 'left' }}>{t('name_english', 'Name (English)')}</Box>}
              {visibleColumns.french && <Box sx={{ flex: 1, paddingLeft: '10px', fontSize: '14px', color: '#fff', textAlign: 'left' }}>{t('name_french', 'Name (French)')}</Box>}
              {visibleColumns.actions && <Box sx={{ textAlign: "center", fontSize: '14px', color: '#fff' }}>{t('actions', 'Actions')}</Box>}
            </Box> */}
          </Box>

          {/* <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
            <TreeView
              defaultExpanded={filteredRows.map((cat) => String(cat.id))}
              aria-label="category tree"
              defaultCollapseIcon={<ExpandMore />}
              defaultExpandIcon={<ChevronRight />}
              sx={{
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '0',
                '& .MuiTreeItem-root:not(.MuiCollapse-root .MuiTreeItem-root)': {
                  minWidth: '600px',
                },
                '& .MuiTreeItem-root:not(:last-child)': {
                  borderBottom: '1px solid rgba(230, 210, 249, 0.2)',
                },
                '& .MuiTreeItem-root .MuiTreeItem-content': {
                  padding: '0',
                },
                '& .MuiTreeItem-root[aria-expanded="true"] .MuiTreeItem-content:not(.MuiCollapse-root .MuiTreeItem-content)': {
                  backgroundColor: 'rgba(255,255,255,0.05)',
                },
                '& .MuiTreeItem-root .MuiTreeItem-content .MuiTreeItem-iconContainer': {
                  padding: density === 'compact' ? '6px' : density === 'comfortable' ? '12px' : '10px',
                  marginRight: '0',
                  width: '38px',
                },
                '& .MuiTreeItem-root .MuiCollapse-root': {
                  marginLeft: '0',
                  paddingLeft: '0px',
                },
                '& .MuiTreeItem-iconContainer svg': {
                  fill: 'white',
                  fontSize: '22px !important',
                },
                '& .MuiTreeItem-content .MuiTreeItem-label': {
                  padding: density === 'compact' ? '6px 0px' : density === 'comfortable' ? '14px 0px' : '10px 0px',
                  color: 'white',
                  fontSize: '14px',
                  width: '100%',
                },
                '& .MuiTreeItem-label .MuiAvatar-root': {
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'rgba(227, 214, 245, 0.2)'
                },
                '& .MuiTreeItem-label .MuiBox-root>*': {
                  padding: '0 10px',
                },
              }}
            >
              {renderTree(filteredRows)}
            </TreeView>
          </DndContext> */}

          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <TreeView
              defaultExpanded={filteredRows.map((cat) =>
                String(cat.id)
              )}
              aria-label="category tree"
              defaultCollapseIcon={<ExpandMore />}
              defaultExpandIcon={<ChevronRight />}
              sx={{
                backgroundColor: "transparent",
                border: "none",
                borderRadius: 0,

                "& .MuiTreeItem-root": {
                  width: "100%",
                  minWidth: "100%",
                  boxSizing: "border-box",
                },
                '& .MuiTreeItem-root:not(:last-child)': {
                  borderBottom: '1px solid rgba(230, 210, 249, 0.2)',
                },
                "& .MuiTreeItem-content": {
                  padding: 0,
                  minHeight:
                    density === "compact"
                      ? "48px"
                      : density === "comfortable"
                        ? "68px"
                        : "58px",
                  display: "flex",
                  alignItems: "stretch",
                  width: "100%",
                },

                "& .MuiTreeItem-content > .MuiTreeItem-iconContainer": {
                  width: "38px",
                  minWidth: "38px",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },

                "& .MuiTreeItem-iconContainer svg": {
                  fill: "#fff",
                  fontSize: "22px !important",
                },

                "& .MuiTreeItem-content > .MuiTreeItem-label": {
                  flex: 1,
                  width: "calc(100% - 38px)",
                  minWidth: 0,
                  padding: 0,
                  color: "#fff",
                  fontSize: "14px",
                },

                "& .MuiTreeItem-group": {
                  marginLeft: 0,
                  paddingLeft: 0,
                },

                "& .MuiCollapse-root": {
                  marginLeft: 0,
                  paddingLeft: 0,
                },
              }}
            >
              {renderTree(filteredRows)}
            </TreeView>
          </DndContext>
        </Box>

        {paginationLoadMore.hasMore && (
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <Button
              variant="outlined"
              onClick={() =>
                setPaginationLoadMore(prev => ({
                  ...prev,
                  page: prev.page + 1
                }))
              }
              disabled={loading}
              sx={{
                color: "#ffffff",
                borderColor: "#FF69B4"
              }}
            >
              {/* {loading ? "Loading..." : "Load More"} */}
              {loading ? t('loading', 'Loading...') : t('load_more', 'Load More')}
            </Button>
          </Box>
        )}
      </Box>


      <Dialog
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleCloseModal();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {editingCategory ? t('edit_category', 'Edit  Category') : t('add_category', 'Add  Category')}
          <IconButton onClick={handleCloseModal} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: 'rgba(230, 210, 249, 0.1)',
            padding: '24px 16px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(230, 210, 249, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(230, 210, 249, 0.5)',
              },
            },
          }}
        >
          <Controller
            name="nameEn"
            control={control}
            // rules={{ required: t('name_english_required', 'English name is required') }}
            rules={{
              required: t(
                'english_name_required',
                'English name is required'
              )
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.nameEn}
                helperText={errors.nameEn?.message}
                placeholder={t('name_english', 'Name (English)')}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 20px',
                    fontWeight: '400',
                    color: '#fff',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                  },
                }}
              />
            )}
          />

          {/* <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  {...field}
                  displayEmpty
                  sx={{
                    color: "#fff",
                    "& .MuiSelect-select": {
                      padding: "12px 20px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1) !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                  }}
                >
                  <MenuItem value="" disabled>{t('select_type', 'Select Type')}</MenuItem>
                  <MenuItem value="PODCAST">Podcast</MenuItem>
                  <MenuItem value="AUDIOBOOK">Audiobook</MenuItem>
                  <MenuItem value="SERIE">Serie</MenuItem>
                </Select>
              </FormControl>
            )}
          /> */}

          {/* <Controller
            name="gradientText"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder={t('gradient_text', 'Gradient Text')}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 20px',
                    fontWeight: '400',
                    color: '#fff',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                  },
                }}
              />
            )}
          /> */}

          <Controller
            name="nameFr"
            control={control}
            rules={{
              required: t(
                'french_name_required',
                'French name is required'
              )
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                error={!!errors.nameFr}
                helperText={errors.nameFr?.message}
                placeholder={t('name_french', 'Name (French)')}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 20px',
                    fontWeight: '400',
                    color: '#fff',
                  },
                  '& input::placeholder': {
                    color: 'rgba(255,255,255,0.6)',
                    opacity: 1,
                  },
                }}
              />
            )}
          />

          {!selectedParent && (!editingCategory || editingCategory.level === 1) && (
            <>
              {/* Two Image Uploads Side by Side */}
              <Box display="flex" gap={2} mb={2}>
                {/* Home Page Image */}
                <Box flex={1} display="flex" flexDirection="column">
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                    {t("administrator.fields.upload_image_title", "Home image")} <Typography component="span" sx={{ fontSize: '13px', color: '#ff4d4f', ml: 1 }}>{t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}</Typography>
                  </Typography>
                  <Box
                    flex={1}
                    component="label"
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleHomeImageChange({ target: { files: [file] } });
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  sx={{
                    border: "1px dashed rgba(230, 210, 249, 0.2)",
                    p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 1, cursor: "pointer", borderRadius: "10px",
                  }}
                >
                  <input hidden type="file" accept="image/*" onChange={handleHomeImageChange} />
                  {homeImagePreview ? (
                    <img src={homeImagePreview} width={60} alt={t('preview', 'Preview')} style={{ borderRadius: '8px', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CloudUpload fontSize="medium" />
                      <Typography variant="body2" sx={{ fontSize: '12px', my: '3px', textAlign: 'center' }}>
                        {t('home_image', 'Home Image')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#ff4d4f', textAlign: 'center', mb: '4px' }}>
                        {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                </Box>

                {/* Search Section Image */}
                <Box flex={1} display="flex" flexDirection="column">
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                    {t("search_image", "Search Image")}
                  </Typography>
                  <Box
                    flex={1}
                    component="label"
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleSearchImageChange({ target: { files: [file] } });
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  sx={{
                    border: "1px dashed rgba(230, 210, 249, 0.2)",
                    p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 1, cursor: "pointer", borderRadius: "10px",
                  }}
                >
                  <input hidden type="file" accept="image/*" onChange={handleSearchImageChange} />
                  {searchImagePreview ? (
                    <img src={searchImagePreview} width={60} alt={t('preview', 'Preview')} style={{ borderRadius: '8px', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <CloudUpload fontSize="medium" />
                      <Typography variant="body2" sx={{ fontSize: '12px', my: '3px', textAlign: 'center' }}>
                        {t('search_image', 'Search Image')}
                      </Typography>
                    </Box>
                  )}
                </Box>
                </Box>
              </Box>

              {/* Background Color Picker */}
              <Box mb={3} display="flex" alignItems="center" gap={4}>
                <Controller
                  name="backgroundColor"
                  control={control}
                  render={({ field }) => (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body1" color="white">{t('background_color', 'Background Color')}:</Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid rgba(230, 210, 249, 0.3)',
                        borderRadius: '4px',
                        width: '130px',
                        height: '40px',
                        backgroundColor: 'transparent'
                      }}>
                        <Typography sx={{ flex: 1, pl: 1.5, color: 'white', fontSize: '14px' }}>
                          {field.value || '#000000'}
                        </Typography>
                        <Box sx={{
                          width: '38px',
                          height: '38px',
                          borderLeft: '1px solid rgba(230, 210, 249, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent'
                        }}>
                          <input
                            type="color"
                            {...field}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              backgroundColor: 'transparent'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
                <Controller
                  name="backgroundColor2"
                  control={control}
                  render={({ field }) => (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid rgba(230, 210, 249, 0.3)',
                        borderRadius: '4px',
                        width: '130px',
                        height: '40px',
                        backgroundColor: 'transparent'
                      }}>
                        <Typography sx={{ flex: 1, pl: 1.5, color: 'white', fontSize: '14px' }}>
                          {field.value || '#000000'}
                        </Typography>
                        <Box sx={{
                          width: '38px',
                          height: '38px',
                          borderLeft: '1px solid rgba(230, 210, 249, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent'
                        }}>
                          <input
                            type="color"
                            {...field}
                            style={{
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              backgroundColor: 'transparent'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Box>

              {/* Live Preview Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" color="white" mb={1}>{t('live_preview', 'Live Preview')}:</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {/* English Preview */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: '200px',
                      maxWidth: '250px',
                      height: '120px',
                      background: `linear-gradient(69deg, ${watchBackgroundColor || '#000000'} 0%, ${watchBackgroundColor2 || '#000000'} 100%)`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      padding: '12px',
                      position: 'relative',
                      overflow: 'hidden',
                      margin: '0 auto'
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        zIndex: 2,
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                        wordBreak: 'break-word',
                        maxWidth: '70%',
                        lineHeight: 1.2,
                        fontSize: "14px"
                      }}
                    >
                      {watchNameEn || t('category_name_en', 'Name (English)')}
                    </Typography>

                    {searchImagePreview && (
                      <img
                        src={searchImagePreview}
                        alt="Search Category"
                        style={{
                          position: 'absolute',
                          right: '10px',
                          bottom: '10px',
                          height: '80%',
                          objectFit: 'contain',
                          zIndex: 1,
                          maxWidth: "30%"
                        }}
                      />
                    )}
                  </Box>

                  {/* French Preview */}
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: '200px',
                      maxWidth: '250px',
                      height: '120px',
                      background: `linear-gradient(69deg, ${watchBackgroundColor || '#000000'} 0%, ${watchBackgroundColor2 || '#000000'} 100%)`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      padding: '12px',
                      position: 'relative',
                      overflow: 'hidden',
                      margin: '0 auto'
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        zIndex: 2,
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                        wordBreak: 'break-word',
                        maxWidth: '70%',
                        lineHeight: 1.2,
                        fontSize: "14px"
                      }}
                    >
                      {watchNameFr || t('category_name_fr', 'Name (French)')}
                    </Typography>

                    {searchImagePreview && (
                      <img
                        src={searchImagePreview}
                        alt="Search Category"
                        style={{
                          position: 'absolute',
                          right: '10px',
                          bottom: '10px',
                          height: '80%',
                          objectFit: 'contain',
                          zIndex: 1,
                          maxWidth: "30%"
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}

          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                {/* <InputLabel>{t('status', 'Status')}</InputLabel> */}
                <Select
                  {...field}
                  // label={t('status', 'Status')}
                  sx={{
                    color: "#fff",
                    fontWeight: 400,
                    "& .MuiSelect-select": {
                      padding: "12px 20px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1) !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(230, 210, 249, 0.1)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                  }}
                >
                  <MenuItem value={true}>{t('active', 'Active')}</MenuItem>
                  <MenuItem value={false}>{t("inactive", "Inactive")}</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
          <Button onClick={handleCloseModal} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit(onSubmit)}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={deleteDialogOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setDeleteDialogOpen(false);
          }
        }}
      >
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t('delete_category', 'Delete Category')}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}><Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{"Are you sure?"}</Typography></DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={deleting}
            onClick={handleDeleteConfirm}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewModalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setViewModalOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {t('view_category', 'View Category')}
          <IconButton onClick={() => setViewModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(230, 210, 249, 0.1)', padding: '24px 16px' }}>
          {selectedCategory && (
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                <strong>{t('name_en', 'Name (English)')}:</strong> {getCategoryName(selectedCategory, "en")}
              </Typography>
              <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                <strong>{t('name_fr', 'Name (French)')}:</strong> {getCategoryName(selectedCategory, "fr")}
              </Typography>

              <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                <strong>{t('status', 'Status')}:</strong> {selectedCategory.isActive ? 'Active' : 'Inactive'}
              </Typography>
              {selectedCategory?.level === 1 && (
                <>
                  <Typography variant="body1" sx={{ color: '#fff', mb: 1, display: 'flex', alignItems: 'center' }}>
                    <strong>{t('colors', 'Colors')}:</strong>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, ml: 1 }}>
                      <Box sx={{ width: 20, height: 20, backgroundColor: selectedCategory.bgColor1 || selectedCategory.backgroundColor || '#000000', borderRadius: '50%', border: '1px solid #fff' }} title="Background Color 1" />
                      <span>{selectedCategory.bgColor1 || selectedCategory.backgroundColor || '#000'}</span>
                      <Box sx={{ width: 20, height: 20, backgroundColor: selectedCategory.bgColor2 || selectedCategory.backgroundColor2 || '#000000', borderRadius: '50%', border: '1px solid #fff' }} title="Background Color 2" />
                      <span>{selectedCategory.bgColor2 || selectedCategory.backgroundColor2 || '#000'}</span>
                    </Box>
                  </Typography>
                  <Box display="flex" gap={2} mt={2}>
                    {(getImageUrl(selectedCategory.featuredImage)) && (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>{t('home_image', 'Home Image')}</Typography>
                        <img src={getImageUrl(selectedCategory.featuredImage)} alt="Home" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                      </Box>
                    )}
                    {(getImageUrl(selectedCategory.image)) && (
                      <Box>
                        <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>{t('search_image', 'Search Image')}</Typography>
                        <img src={getImageUrl(selectedCategory.image)} alt="Search" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                      </Box>
                    )}
                  </Box>
                  {/* Live Preview Section for View Modal */}
                  <Box mt={3} mb={1}>
                    <Typography variant="subtitle1" color="white" mb={1}>{t('live_preview', 'Live Preview')}:</Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      {/* English Preview */}
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: '200px',
                          maxWidth: '250px',
                          height: '120px',
                          background: `linear-gradient(69deg, ${selectedCategory.bgColor1 || selectedCategory.backgroundColor || '#000000'} 0%, ${selectedCategory.bgColor2 || selectedCategory.backgroundColor2 || '#000000'} 100%)`,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          padding: '12px',
                          position: 'relative',
                          overflow: 'hidden',
                          margin: '0 auto'
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            zIndex: 2,
                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                            wordBreak: 'break-word',
                            maxWidth: '70%',
                            lineHeight: 1.2,
                            fontSize: "14px"
                          }}
                        >
                          {getCategoryName(selectedCategory, "en") || t('category_name_en', 'Name (English)')}
                        </Typography>

                        {getImageUrl(selectedCategory.image) && (
                          <img
                            src={getImageUrl(selectedCategory.image)}
                            alt="Search Category"
                            style={{
                              position: 'absolute',
                              right: '10px',
                              bottom: '10px',
                              height: '80%',
                              objectFit: 'contain',
                              zIndex: 1,
                              maxWidth: "30%"
                            }}
                          />
                        )}
                      </Box>

                      {/* French Preview */}
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: '200px',
                          maxWidth: '250px',
                          height: '120px',
                          background: `linear-gradient(69deg, ${selectedCategory.bgColor1 || selectedCategory.backgroundColor || '#000000'} 0%, ${selectedCategory.bgColor2 || selectedCategory.backgroundColor2 || '#000000'} 100%)`,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          padding: '12px',
                          position: 'relative',
                          overflow: 'hidden',
                          margin: '0 auto'
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            zIndex: 2,
                            textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                            wordBreak: 'break-word',
                            maxWidth: '70%',
                            lineHeight: 1.2,
                            fontSize: "14px"
                          }}
                        >
                          {getCategoryName(selectedCategory, "fr") || t('category_name_fr', 'Name (French)')}
                        </Typography>

                        {getImageUrl(selectedCategory.image) && (
                          <img
                            src={getImageUrl(selectedCategory.image)}
                            alt="Search Category"
                            style={{
                              position: 'absolute',
                              right: '10px',
                              bottom: '10px',
                              height: '80%',
                              objectFit: 'contain',
                              zIndex: 1,
                              maxWidth: "30%"
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
          <Button onClick={() => setViewModalOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <GlobalStyles
        styles={{
          '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
            backgroundColor: '#252525 !important',
            border: '1px solid rgba(230, 210, 249, 0.1)',
            fontSize: '15px',
            color: '#fff',
            borderRadius: '4px',
          },
          '.css-1gqihfy': {
            paddingLeft: '10px !important',
          },
          '.MuiDialog-root .MuiPaper-root': {
            borderRadius: '20px',
          },
          '.MuiDataGrid-columnsManagementHeader': {
            padding: '10px !important',
          },
          '.MuiInputBase-formControl': {
            fontSize: '14px !important',
            color: 'white !important',
            borderRadius: '999px !important',
          },
          '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
            padding: '4px 8px 4px 16px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
            padding: '0px 16px 12px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
            fontSize: '14px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
            color: '#FF69B4',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
            color: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
            borderColor: 'rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
            display: 'none',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
            padding: '0',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
            fontSize: '14px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
            padding: '5px 10px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: '#FF69B4',
          },
          '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
            borderColor: 'rgba(230, 210, 249, 0.3) !important',
            margin: '0',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            minWidth: '32px',
          },
        }}
      />
    </Box>
  );
}

export default CategoryTreeManagement;
